import React, { useMemo } from "react";
import SkeletonCard from "./common/SkeletonCard";
import EmptyState from "./common/EmptyState";
import DashboardCard from "./common/DashboardCard";
import { canSeeMargin } from "../utils/roleUtils";

function formatCurrency(value, displayCurrency = "AED") {
    if (value == null) return "-";
    return `${displayCurrency} ${Math.round(Number(value)).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function GoodPairs({ filterContext, onMaximize, fullScreen = false, highPriorityEnquiries = [], loading = false, error = "" }) {
    const { salesperson, year, region, displayCurrency = "AED" } = filterContext || {};
    const list = Array.isArray(highPriorityEnquiries) ? highPriorityEnquiries : [];

    const normalizedRows = useMemo(() => (
        list.map((item) => {
            const apiCurrency = item.display_currency || item.displayCurrency;
            const rowDisplayCurrency = apiCurrency || displayCurrency;
            const displayPrice = item.display_price ?? item.price_aed;
            const displayMarginValue = item.display_margin_value ?? item.margin_aed;
            return {
                ...item,
                displayCurrency: rowDisplayCurrency,
                displayPrice,
                displayMarginValue,
            };
        })
    ), [list, displayCurrency]);

    const sortedGoodRows = useMemo(() => (
        [...normalizedRows].sort((a, b) => (b.price_aed || 0) - (a.price_aed || 0))
    ), [normalizedRows]);

    const hasData = sortedGoodRows.length > 0;

    const actionButtons = useMemo(() => (
        <>
            {onMaximize && !fullScreen && (
                <button
                    onClick={onMaximize}
                    style={{
                        padding: "8px",
                        background: "rgba(255, 255, 255, 0.05)",
                        color: "#e5e7eb",
                        borderRadius: 8,
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        transition: "all 0.2s ease"
                    }}
                    title="Maximize"
                >
                    <span style={{ fontSize: "16px", lineHeight: 1 }}>⤢</span>
                </button>
            )}

            {fullScreen && onMaximize && (
                <button
                    onClick={onMaximize}
                    style={{
                        padding: "8px",
                        background: "rgba(244, 67, 54, 0.2)",
                        color: "#f44336",
                        border: "1px solid #f44336",
                        borderRadius: "50%",
                        width: "36px",
                        height: "36px",
                        cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        transition: "all 0.2s ease"
                    }}
                >

                </button>
            )}
        </>
    ), [onMaximize, fullScreen]);

    const Subtitle = () => (
        <p
            style={{
                color: "#94a3b8",
                fontSize: fullScreen ? "14px" : "13px",
                margin: "0 0 16px 0",
            }}
        >
            Salesperson: <strong style={{ color: "#e2e8f0" }}>{salesperson}</strong> · Region:{" "}
            <strong style={{ color: "#e2e8f0" }}>{region === "all" ? "All Regions" : region}</strong>
        </p>
    );

    const StatsHeader = () => (
        <div
            style={{
                marginBottom: 16,
                display: "flex",
                flexWrap: "wrap",
                gap: "12px",
                alignItems: "center",
            }}
        >
            <span
                style={{
                    padding: "6px 12px",
                    borderRadius: 999,
                    background: "rgba(16, 185, 129, 0.15)",
                    border: "1px solid rgba(16, 185, 129, 0.3)",
                    color: "#34d399",
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: 0.3,
                    textTransform: "uppercase",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                }}
            >
                High Priority Enquiries: {list.length}
            </span>
        </div>
    );

    const showMargin = useMemo(() => canSeeMargin(), []);

    const TableContent = () => (
        <table
            style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: "0",
                fontSize: 13,
                color: "#e5e7eb",
            }}
        >
            <thead>
                <tr>
                    <th style={{ textAlign: "left", padding: "16px", borderBottom: "1px solid rgba(255,255,255,0.1)", fontWeight: 600, color: "#94a3b8", whiteSpace: "nowrap", background: "rgba(15, 23, 42, 0.5)" }}>Opp No</th>
                    <th style={{ textAlign: "left", padding: "16px", borderBottom: "1px solid rgba(255,255,255,0.1)", fontWeight: 600, color: "#94a3b8", background: "rgba(15, 23, 42, 0.5)" }}>Opp Name</th>
                    <th style={{ textAlign: "right", padding: "16px", borderBottom: "1px solid rgba(255,255,255,0.1)", fontWeight: 600, color: "#94a3b8", whiteSpace: "nowrap", background: "rgba(15, 23, 42, 0.5)" }}>Price</th>
                    {showMargin && (
                        <th style={{ textAlign: "right", padding: "16px", borderBottom: "1px solid rgba(255,255,255,0.1)", fontWeight: 600, color: "#94a3b8", whiteSpace: "nowrap", background: "rgba(15, 23, 42, 0.5)" }}>Margin</th>
                    )}
                    <th style={{ textAlign: "center", padding: "16px", borderBottom: "1px solid rgba(255,255,255,0.1)", fontWeight: 600, color: "#94a3b8", whiteSpace: "nowrap", background: "rgba(15, 23, 42, 0.5)" }}>Status</th>
                </tr>
            </thead>
            <tbody>
                {sortedGoodRows.map((row, idx) => {
                    const pctRaw = typeof row.margin_pct === "number" ? row.margin_pct : row.price_aed > 0 ? (row.margin_aed / row.price_aed) * 100 : null;
                    const pct = pctRaw !== null && pctRaw !== undefined ? pctRaw.toFixed(1) : null;
                    return (
                        <tr key={`good-${row.opportunity_code}-${idx}`} style={{ transition: "background 0.2s" }} className="hover-row">
                            <td style={{ padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.05)", fontWeight: 500 }}>{row.opportunity_code || "-"}</td>
                            <td style={{ padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.05)", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.opportunity_name || ""}>{row.opportunity_name || "-"}</td>
                            <td style={{ padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.05)", textAlign: "right", fontWeight: 500 }}>
                                {formatCurrency(row.displayPrice, row.displayCurrency || displayCurrency)}
                            </td>
                            {showMargin && (
                                <td style={{ padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.05)", textAlign: "right", color: "#34d399", fontWeight: 700 }}>{pct !== null ? `${pct}%` : "-"}</td>
                            )}
                            <td style={{ padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.05)", textAlign: "center" }}>
                                <span style={{
                                    padding: "4px 12px",
                                    borderRadius: 6,
                                    background: "rgba(59, 130, 246, 0.2)",
                                    color: "#60a5fa",
                                    fontSize: 11,
                                    fontWeight: 600,
                                    border: "1px solid rgba(59, 130, 246, 0.3)"
                                }}>
                                    {row.stage || "Unknown"}
                                </span>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );

    if (!salesperson || !year) return null;

    if (fullScreen) {
        return (
            <div style={{ width: "100%", height: "100%", padding: "20px", background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", position: "relative", display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                    <div>
                        <h2 style={{ fontSize: "32px", margin: 0, color: "#fff", fontWeight: 800 }}>High Priority Enquiries – {year}</h2>
                        <Subtitle />
                    </div>
                    <div style={{ display: "flex", gap: "10px" }}>
                        {actionButtons}
                    </div>
                </div>

                {loading && <SkeletonCard height="200px" />}
                {error && <EmptyState icon="" message={error} size="small" />}
                {!loading && !error && (
                    <>
                        <StatsHeader />
                        <div style={{ flex: 1, overflow: "auto", background: "rgba(30, 41, 59, 0.4)", borderRadius: "16px", border: "1px solid rgba(255,255,255,0.05)" }}>
                            <TableContent />
                        </div>
                    </>
                )}
            </div>
        );
    }

    return (
        <DashboardCard
            title={`High Priority Enquiries – ${year}`}
            actions={actionButtons}
            gradient="success"
        >
            <Subtitle />

            {loading && <SkeletonCard height="300px" />}

            {error && (
                <EmptyState icon="️" message={error} size="small" />
            )}

            {!loading && !error && !hasData && (
                <EmptyState icon="" message="No high priority enquiries found." size="medium" />
            )}

            {!loading && !error && hasData && (
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <StatsHeader />
                    <div style={{ flex: 1, overflow: "auto", margin: "-10px -24px 0 -24px", padding: "0 24px" }}>
                        <TableContent />
                    </div>
                </div>
            )}
        </DashboardCard>
    );
}

export default GoodPairs;
